import React, { useState } from 'react';
import styled from '@emotion/styled';
import { HeadingText20, BodyTextMedium, HeadingText60 } from '@modernhealth/vital-components';
import { Route, Switch, useRouteMatch, useHistory, Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { mediaQuery, colors } from '@modernhealth/vital-foundation';
import moment from 'moment';

import { useLocale } from 'src/shared/intl/translations';

import DisplayUpcomingBySeries from '../components/DisplayUpcomingBySeries';
import DisplayUpcomingByTime from '../components/DisplayUpcomingByTime';

export const SingleLineText = styled(BodyTextMedium)({
  // -webkit-line-clamp is actually part of the CSS standard
  // See: https://developer.mozilla.org/en-US/docs/Web/CSS/-webkit-line-clamp
  display: '-webkit-box',
  lineClamp: 1,
  WebkitLineClamp: 1,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

export const LinkHeader = styled(Link)({
  color: colors.gray85,
  fontSize: 22,
  fontWeight: 700,
  lineHeight: '1.5rem',
  '&:hover': {
    color: colors.gray85,
    textDecoration: 'underline',
  },
});

export const UnderlinedLink = styled(Link)({
  whiteSpace: 'nowrap',
  marginLeft: 10,
  textDecoration: 'underline',
});

export const UpcomingCirclesRowSubheader = styled.div({
  display: 'flex',
  flex: 'row nowrap',
  justifyContent: 'space-between',
});

export const UpcomingCirclesRow = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr 0 0',
  gridTemplateRows: '1fr 0',
  gap: 0,
  overflow: 'hidden',
  [mediaQuery.minWidth('md')]: {
    gap: '0 1rem',
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  [mediaQuery.minWidth('lg')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
});

const Wrapper = styled.div({
  width: '100%',
});

const DisplayTypes = styled.div({
  display: 'flex',
  gap: 9,
  marginBottom: 48,
});

interface DisplayTabProps {
  active: boolean;
}

const DisplayTab = styled.span<DisplayTabProps>`
  width: 85px;
  height: 32px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 6px;
  background-color: ${props => {
    return props.active ? colors.gray85 : 'transparent';
  }};
  &:hover {
    background-color: ${props => {
      return props.active ? colors.gray85 : colors.gray05;
    }};
  }
`;

const UpcomingCirclesPage: React.FC = () => {
  const history = useHistory();
  const { locale } = useLocale();
  const { path } = useRouteMatch();
  const localizedVersion = locale !== 'en-US';
  const localizedPath = !localizedVersion ? `${path}/series/` : '*';
  const [currentMoment] = useState(moment().toISOString());
  // TODO: Remove loacalizedVersion logic from this file once translations are ready for the new strings
  const isDisplayByTime = !!useRouteMatch({ path: `${path}/`, exact: true });

  return (
    <Wrapper>
      <HeadingText60 as="h2" fontColor="gray85" css={{ marginTop: 64, marginBottom: 54 }}>
        <FormattedMessage defaultMessage="Upcoming Circles" />
      </HeadingText60>
      {!localizedVersion && (
        <DisplayTypes>
          <DisplayTab
            onClick={() => {
              history.replace('/upcoming');
            }}
            active={isDisplayByTime}
          >
            <HeadingText20 marginBottom={0} fontColor={isDisplayByTime ? 'white' : 'gray85'}>
              <FormattedMessage defaultMessage="By time" />
            </HeadingText20>
          </DisplayTab>
          <DisplayTab
            onClick={() => {
              history.replace('/upcoming/series');
            }}
            active={!isDisplayByTime}
          >
            <HeadingText20 marginBottom={0} fontColor={!isDisplayByTime ? 'white' : 'gray85'}>
              <FormattedMessage defaultMessage="By series" />
            </HeadingText20>
          </DisplayTab>
        </DisplayTypes>
      )}
      <Switch>
        {!localizedVersion ? (
          <Route
            path={`${path}`}
            exact
            render={() => <DisplayUpcomingByTime currentMoment={currentMoment} />}
          />
        ) : null}
        <Route path={localizedPath} component={DisplayUpcomingBySeries} exact />
      </Switch>
    </Wrapper>
  );
};

export default UpcomingCirclesPage;
